import { LiveData, Service } from '@toeverything/infra';
import { cssVar } from '@toeverything/theme';
import dayjs from 'dayjs';

import type { DocsService } from '../../doc';
import type { Tag, TagService } from '../../tag';
import type { TemplateDocService } from '../../template-doc';
import type { JournalStore } from '../store/journal';

export type MaybeDate = Date | string | number;

export const JOURNAL_DATE_FORMAT = 'YYYY-MM-DD';
const JOURNAL_TAG_NAME = 'journal';

export class JournalService extends Service {
  constructor(
    private readonly store: JournalStore,
    private readonly docsService: DocsService,
    private readonly templateDocService: TemplateDocService,
    private readonly tagService: TagService
  ) {
    super();
  }

  allJournalDates$ = this.store.allJournalDates$;

  /**
   * Journals created from a template are populated asynchronously, and
   * `duplicateFromTemplate` clears the target's root children before inserting.
   * Anything that writes into a freshly created journal has to wait for that,
   * or its blocks get wiped. Keyed by doc id, cleared once settled.
   */
  private readonly initializing = new Map<string, Promise<void>>();
  private readonly tokens = new Map<string, symbol>();

  journalDate$(docId: string) {
    return LiveData.from(this.store.watchDocJournalDate(docId), undefined);
  }
  journalToday$(docId: string) {
    return LiveData.computed(get => {
      const date = get(this.journalDate$(docId));
      if (!date) return false;
      return dayjs(date).isSame(dayjs(), 'day');
    });
  }

  setJournalDate(docId: string, date: string) {
    this.store.setDocJournalDate(docId, date);
    this.applyJournalTag(docId);
  }

  removeJournalDate(docId: string) {
    this.store.removeDocJournalDate(docId);
    this.removeJournalTag(docId);
  }

  private ensureJournalTag(): Tag {
    const tagList = this.tagService.tagList;
    const existingTags = tagList.tags$.value;
    const found = existingTags.find(
      tag => tag.value$.value.trim().toLowerCase() === JOURNAL_TAG_NAME
    );
    if (found) {
      return found;
    }
    return tagList.createTag(JOURNAL_TAG_NAME, cssVar('paletteLineBlue'));
  }

  private applyJournalTag(docId: string) {
    const journalTag = this.ensureJournalTag();
    const pageRecord = this.docsService.list.doc$(docId).value;
    if (!pageRecord) return;
    const existingTags = pageRecord.meta$.value.tags ?? [];
    if (existingTags.includes(journalTag.id)) return;
    journalTag.tag(docId);
  }

  private removeJournalTag(docId: string) {
    const tagList = this.tagService.tagList;
    const existingTags = tagList.tags$.value;
    const journalTag = existingTags.find(
      tag => tag.value$.value.trim().toLowerCase() === JOURNAL_TAG_NAME
    );
    if (!journalTag) return;
    journalTag.untag(docId);
  }

  journalsByDate$(date: string) {
    return this.store.docsByJournalDate$(date);
  }

  private createJournal(maybeDate: MaybeDate) {
    const day = dayjs(maybeDate);
    const title = day.format(JOURNAL_DATE_FORMAT);
    const docRecord = this.docsService.createDoc({
      title,
    });
    // set created date to match the journal date
    docRecord.setMeta({
      createDate: dayjs()
        .set('year', day.year())
        .set('month', day.month())
        .set('date', day.date())
        .toDate()
        .getTime(),
    });

    const enablePageTemplate =
      this.templateDocService.setting.enablePageTemplate$.value;
    const pageTemplateDocId =
      this.templateDocService.setting.pageTemplateDocId$.value;
    const journalTemplateDocId =
      this.templateDocService.setting.journalTemplateDocId$.value;
    // if journal template configured
    const templateDocId = journalTemplateDocId
      ? journalTemplateDocId
      : // journal template not configured, use page template
        enablePageTemplate && pageTemplateDocId
        ? pageTemplateDocId
        : undefined;
    if (templateDocId) {
      this.trackInitialization(
        docRecord.id,
        this.docsService
          .duplicateFromTemplate(templateDocId, docRecord.id)
          .then(() => undefined)
      );
    }
    this.setJournalDate(docRecord.id, title);
    return docRecord;
  }

  private trackInitialization(docId: string, promise: Promise<void>) {
    const token = Symbol(docId);
    this.tokens.set(docId, token);
    const tracked = promise.catch(console.error).then(() => {
      if (this.tokens.get(docId) === token) {
        this.tokens.delete(docId);
        this.initializing.delete(docId);
      }
    });
    this.initializing.set(docId, tracked);
  }

  /**
   * Resolves once a journal created by {@link ensureJournalByDate} has finished
   * applying its template. Resolves immediately for journals that already
   * existed or were created without a template.
   */
  whenJournalReady(docId: string): Promise<void> {
    return this.initializing.get(docId) ?? Promise.resolve();
  }

  ensureJournalByDate(maybeDate: MaybeDate) {
    const day = dayjs(maybeDate);
    const title = day.format(JOURNAL_DATE_FORMAT);
    const doc = this.store.findDocByJournalDate(title);
    if (doc) return doc;
    return this.createJournal(maybeDate);
  }
}
